import { ReactNode } from 'react';

import { VisibilityState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/ui/table/data-table';
import { AIAgent } from '@/shared/types/brain';

import { columns } from './columns';
import { useAgentFavorites } from '../../hooks/useAgentFavorites';

interface AIAgentsTableProps {
  data?: AIAgent[];
  isLoading?: boolean;
  getRowHref?: (row: AIAgent) => string | null;
  getRowLinkState?: (row: AIAgent) => object | undefined;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  initialPageIndex?: number;
  onPageChange?: (pageIndex: number) => void;
  paginationCenterContent?: ReactNode;
  activeRowId?: string;
  className?: string;
}

export const AIAgentsTable = ({
  data,
  isLoading,
  getRowHref,
  getRowLinkState,
  columnVisibility,
  onColumnVisibilityChange,
  initialPageIndex,
  onPageChange,
  paginationCenterContent,
  activeRowId,
  className,
}: AIAgentsTableProps = {}) => {
  const { t } = useTranslation();

  const { favoriteIds, toggleFavorite } = useAgentFavorites();

  return (
    <DataTable.Root
      fill
      className={className}
      columns={columns({ toggleFavorite, favoriteIds })}
      data={data}
      rowCount={data?.length}
      resourceName="ai_agent"
      emptyStateMessage={t('ai_agents.no_agents_found')}
      isLoading={isLoading}
      getRowId={(row: AIAgent) => row.brain_id}
      activeRowId={activeRowId}
      enableRowHover={true}
      getRowHref={getRowHref}
      getRowLinkState={getRowLinkState}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
      initialPageIndex={initialPageIndex}
      onPageChange={onPageChange}
      autoResetPageIndex
    >
      <DataTable.Content />
      <DataTable.Pagination centerContent={paginationCenterContent} />
    </DataTable.Root>
  );
};
